import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { type PerformanceCompetitiveness } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceCompetitiveness";
import { formatPercent } from "@/App/Views/carrierFormatting";
import { PerformanceMetric } from "@/App/Performance/PerformanceMetric";

/**
 * Where this carrier's prices land against the other carriers the customer saw.
 *
 * This is the part of the page a carrier can act on. On-time performance and
 * volume tell them how they did; price position tells them why they won or lost
 * the freight, which is the only lever they have with us — customers pick the
 * carrier, so a carrier grows with us by quoting competitively, not by asking
 * for more freight.
 *
 * No dollar figures anywhere. A carrier knows its own rate, so showing our price
 * on it reveals our markup exactly, and no other carrier is named or identifiable.
 */
export function CompetitivenessPanel({
  competitiveness,
}: {
  competitiveness: PerformanceCompetitiveness;
}) {
  const c = competitiveness;

  return (
    <Stack gap={4}>
      <Panel
        title="Your Price Position"
        description="How your pricing compared with the other carriers quoting the same freight."
      >
        <Grid columns={3} gap="md">
          <PerformanceMetric
            label="Cheapest"
            value={formatRate_(c.cheapestRate)}
            hint={`of ${c.quotedShipments} shipments you priced`}
            explanation="The share of the shipments you priced where yours was the lowest price the customer saw. Ties count as cheapest for everyone tied — nobody beat you on price."
          />
          <PerformanceMetric
            label="Average Rank"
            value={c.averagePriceRank ? c.averagePriceRank.toFixed(1) : "—"}
            hint={
              c.averageCarriersQuoting
                ? `of ${c.averageCarriersQuoting.toFixed(1)} carriers quoting`
                : "1 is cheapest"
            }
            explanation="Your average position on the quote boards the customer saw, where 1 is cheapest. Averaged across every quote, not deduplicated by shipment. Carriers tied on price share a rank, so two at the lowest price are both 1 and the next is 3."
          />
          <PerformanceMetric
            label="Premium When Not Cheapest"
            value={formatRate_(c.averagePremiumWhenNotCheapest)}
            hint="above the lowest price shown"
            explanation="On the shipments where you were not cheapest, how far your best price sat above the lowest one the customer saw, on average. 100% means double. Shipments where you were cheapest are excluded — including them would fold in zeros and understate the gap. Withheld entirely below a few shipments, because an average over one or two would let you work out another carrier's exact price."
          />
        </Grid>
      </Panel>

      <Panel
        title="Winning"
        description="Whether the freight follows the lowest price, or something else."
      >
        <Grid columns={3} gap="md">
          <PerformanceMetric
            label="Win Rate"
            value={formatRate_(winRate_(c))}
            hint={`${c.wonShipments} of ${c.quotedShipments} you priced`}
            explanation="Of the distinct shipments you priced, the share the customer booked with you. Every shipment you quoted is in the denominator, including ones nobody ever booked. Re-quotes of the same real shipment count once."
          />
          <PerformanceMetric
            label="When You Were Cheapest"
            value={formatRate_(c.winRateWhenCheapest)}
            hint="below 100% means customers paid more to go elsewhere"
            explanation="Win rate across only the shipments where you held the lowest price the customer saw. Same denominator rule as the headline: every such shipment counts, booked or not."
          />
          <PerformanceMetric
            label="When You Weren't"
            value={formatRate_(c.winRateWhenNotCheapest)}
            hint="freight you won on something other than price"
            explanation="Win rate across only the shipments where you priced above the lowest quote. Wins here came from transit time or the customer's preference, not price."
          />
        </Grid>
        {/* Said explicitly because it is the single most misread number on the
            page — and because we used to state the opposite. */}
        <T size="sm" color="muted">
          Win rate counts every shipment you priced. Freight you quoted that the
          customer never booked with you counts as a loss, whether they booked
          it elsewhere or didn&rsquo;t ship it at all.
        </T>
      </Panel>

      <Panel
        title="Coverage"
        description="How much of our freight you're pricing at all."
      >
        <PerformanceMetric
          label="Participation"
          value={formatRate_(c.participation)}
          hint="of everything we quoted in this period"
          explanation="The shipments you priced divided by every distinct shipment quoted on the platform in this window. The share of our freight you put a price on at all — before any question of whether you won it."
        />
        <T size="sm" color="muted">
          A low figure usually means lanes, weights or services you don&rsquo;t
          cover, or quotes that failed — not pricing. Sharper rates won&rsquo;t
          move it.
        </T>
      </Panel>
    </Stack>
  );
}

// The headline rate, derived rather than sent twice — wins over every shipment
// priced, exactly what the two counts beside it say.
function winRate_(c: PerformanceCompetitiveness): number | undefined {
  if (c.quotedShipments === 0) return undefined;
  return c.wonShipments / c.quotedShipments;
}

// An absent rate means an empty denominator, not zero. "—" says we have nothing
// to go on; "0%" would say they lose everything.
function formatRate_(value: number | undefined): string {
  return value === undefined ? "—" : formatPercent(value);
}
